/**
 * API client.
 *
 * The access token is held in memory only — never localStorage, which any XSS
 * can read. The refresh token lives in an httpOnly cookie the browser sends
 * automatically and JavaScript cannot touch at all.
 *
 * On a 401 the client refreshes once and retries. Concurrent 401s share a
 * single refresh: without that, ten parallel requests would fire ten
 * rotations, and rotation-with-reuse-detection would read the second as a
 * stolen token and revoke the whole session.
 */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly fields?: Array<{ field: string; message: string }>,
    readonly requestId?: string,
    /**
     * The whole error body.
     *
     * Some refusals carry structured detail the caller has to act on rather
     * than merely display — a duplicate import returns what it clashed with,
     * so the screen can offer replace or skip instead of just saying no.
     */
    readonly payload?: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

let accessToken: string | null = null;
let refreshInFlight: Promise<boolean> | null = null;
let onAuthLost: (() => void) | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

/** Called when refresh fails, so the app can send the user back to sign-in. */
export function onSessionExpired(handler: () => void): void {
  onAuthLost = handler;
}

interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  /** Internal: prevents infinite retry loops. */
  _retried?: boolean;
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { body, _retried, headers, ...rest } = options;

  const isFormData = body instanceof FormData;

  // Built up rather than declared inline: with exactOptionalPropertyTypes,
  // `body: undefined` is not the same as omitting `body`, and fetch rejects
  // the former. Let the browser set Content-Type for FormData so it can add
  // the multipart boundary.
  const init: RequestInit = {
    ...rest,
    credentials: 'include',
    headers: {
      ...(!isFormData && body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      ...(headers as Record<string, string> | undefined),
    },
  };

  if (body !== undefined) {
    init.body = isFormData ? body : JSON.stringify(body);
  }

  /**
   * A request that never reached the server is not the same as one the server
   * rejected, and every caller in the app was treating them alike: `caught
   * instanceof ApiError` is false for a network failure, so the fallback
   * message ran and the user was told "Please try again" for something that
   * will fail identically every time until the API is running again.
   *
   * Wrapping it in an ApiError with status 0 means one shape reaches every
   * caller, and the message says the thing worth knowing.
   */
  let response: Response;
  try {
    response = await fetch(path, init);
  } catch {
    throw new ApiError(
      0,
      'Could not reach the server. It may be restarting — check your connection and try again.',
    );
  }

  if (response.status === 401 && !_retried && !path.includes('/auth/')) {
    const refreshed = await refreshOnce();
    if (refreshed) return request<T>(path, { ...options, _retried: true });
    onAuthLost?.();
    throw new ApiError(401, 'Your session has expired. Please sign in again.');
  }

  if (response.status === 204) return undefined as T;

  const payload: unknown = await response.json().catch(() => null);

  if (!response.ok) {
    const error = (payload ?? {}) as {
      message?: string;
      errors?: Array<{ field: string; message: string }>;
      requestId?: string;
    };
    throw new ApiError(
      response.status,
      error.message ?? `Request failed (${response.status})`,
      error.errors,
      error.requestId,
      (payload ?? undefined) as Record<string, unknown> | undefined,
    );
  }

  return payload as T;
}

/**
 * A download or preview, with the same session handling as everything else.
 *
 * These cannot go through `request` because the body is bytes, not JSON — a
 * workbook, a PDF, a scan. They were each calling `fetch` directly with
 * `getAccessToken()`, which works right up until the access token ages out
 * fifteen minutes after sign-in. After that, exporting a workbook, exporting a
 * PDF and opening an uploaded file all failed together with a generic message,
 * on a tab that had simply been left open — while every ordinary action on the
 * same screen kept working, because those refresh and retry.
 *
 * One 401 retry, sharing the same coalesced refresh, and the caller gets the
 * Response to read as it likes.
 */
export async function authedFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const send = (): Promise<Response> =>
    fetch(path, {
      ...init,
      credentials: 'include',
      headers: {
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        ...(init.headers as Record<string, string> | undefined),
      },
    });

  let response: Response;
  try {
    response = await send();
  } catch {
    throw new ApiError(
      0,
      'Could not reach the server. It may be restarting — check your connection and try again.',
    );
  }

  if (response.status === 401) {
    const refreshed = await refreshOnce();
    if (!refreshed) {
      onAuthLost?.();
      throw new ApiError(401, 'Your session has expired. Please sign in again.');
    }
    response = await send();
  }

  if (!response.ok) {
    // The body is usually JSON even when the request was for bytes.
    const payload = (await response.json().catch(() => null)) as { message?: string } | null;
    throw new ApiError(response.status, payload?.message ?? `Request failed (${response.status})`);
  }

  return response;
}

/**
 * A new access token from the refresh cookie — or null if the session is over.
 *
 * Over means the server said so: 401 or 403. A server that could not be
 * reached, or answered 5xx, is a server restarting, not a session ending, and
 * is asked again for about half a minute. Treating the two alike is what
 * signed everyone out during the 21 Sep outage: every page opened while the
 * API was coming back up found "no session" and went to the sign-in screen.
 */
export async function fetchAccessToken(): Promise<string | null> {
  const waits = [1_000, 2_000, 4_000, 8_000, 15_000];
  for (let attempt = 0; ; attempt += 1) {
    let response: Response | null = null;
    try {
      response = await fetch('/api/auth/refresh', { method: 'POST', credentials: 'include' });
    } catch {
      response = null;
    }
    if (response?.ok) {
      const { accessToken: token } = (await response.json()) as { accessToken: string };
      return token;
    }
    const passing = response === null || response.status >= 500 || response.status === 429;
    const wait = waits[attempt];
    if (!passing || wait === undefined) return null;
    await new Promise((resolve) => setTimeout(resolve, wait));
  }
}

/**
 * Renew the session, for callers that send their own requests.
 *
 * The upload queue does: it sends bytes with XHR for the progress events,
 * outside `request` and its 401 retry. A folder upload that outlived the
 * fifteen-minute access token then failed every file after that moment with
 * "Unauthorized" — the silent half of a big upload going missing.
 */
export function refreshSession(): Promise<boolean> {
  return refreshOnce();
}

/**
 * Refresh, coalescing concurrent callers onto one in-flight attempt.
 */
async function refreshOnce(): Promise<boolean> {
  refreshInFlight ??= (async () => {
    try {
      const token = await fetchAccessToken();
      if (!token) return false;
      setAccessToken(token);
      return true;
    } catch {
      return false;
    } finally {
      // Cleared on the next tick so callers awaiting this promise all observe
      // the same result before a new attempt can start.
      queueMicrotask(() => {
        refreshInFlight = null;
      });
    }
  })();

  return refreshInFlight;
}

/**
 * An upload that reports how far it has got.
 *
 * XMLHttpRequest rather than fetch, for one reason: fetch cannot tell you how
 * much of a request body has been sent. Without that a 40 MB scan of receipts
 * is a spinner that sits there, and the only honest thing the UI can say is
 * "working" — which is exactly what makes people click Upload a second time.
 *
 * Two phases are visible to the caller. `onProgress` runs while the bytes go
 * up; `onSent` fires when the last byte is gone and the wait becomes the
 * server's — scanning, converting, and thumbnailing — which has no progress to
 * report and should be shown as indeterminate rather than as a stuck 100%.
 */
export function uploadWithProgress<T>(
  path: string,
  form: FormData,
  options: {
    onProgress?: (loaded: number, total: number) => void;
    onSent?: () => void;
    signal?: AbortSignal;
  } = {},
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', path);
    xhr.withCredentials = true;
    if (accessToken) xhr.setRequestHeader('Authorization', `Bearer ${accessToken}`);

    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) options.onProgress?.(event.loaded, event.total);
    };
    xhr.upload.onload = () => options.onSent?.();

    xhr.onload = () => {
      const payload: unknown = xhr.responseText
        ? ((): unknown => {
            try {
              return JSON.parse(xhr.responseText);
            } catch {
              return null;
            }
          })()
        : null;

      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(payload as T);
        return;
      }

      const error = (payload ?? {}) as {
        message?: string;
        errors?: Array<{ field: string; message: string }>;
        requestId?: string;
      };
      reject(
        new ApiError(
          xhr.status,
          error.message ?? `Upload failed (${xhr.status})`,
          error.errors,
          error.requestId,
          (payload ?? undefined) as Record<string, unknown> | undefined,
        ),
      );
    };

    xhr.onerror = () =>
      reject(
        new ApiError(
          0,
          'Could not reach the server. It may be restarting — check your connection and try again.',
        ),
      );
    xhr.onabort = () => reject(new ApiError(0, 'Upload cancelled.'));

    options.signal?.addEventListener('abort', () => xhr.abort(), { once: true });
    xhr.send(form);
  });
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) => request<T>(path, { method: 'POST', body }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
  upload: <T>(path: string, form: FormData) => request<T>(path, { method: 'POST', body: form }),
};
